import type { BoardToolId } from "./types";
import type { SceneHost } from "./SceneHost";
import {
  boardCursorSurfaces,
  clearShapeKindCursorClasses,
  syncShapeKindCursorClass,
} from "./boardCursorSurfaces";
import { getShapeSettings, shapeSettingsEventName } from "./shapeSession";
import { shouldReleaseFocus, shouldReleaseFocusOnHover } from "./keyboardFocus";
import { bindSelectTool, type SelectToolBinding } from "./tools/selectTool";
import { bindPenTool, type PenToolBinding } from "./tools/penTool";
import { bindEraserTool, type EraserToolBinding } from "./tools/eraserTool";
import { bindHandTool, type HandToolBinding } from "./tools/handTool";

import { bindLineTool, bindArrowTool } from "./tools/lineTool";
import { bindShapeTool } from "./tools/shapeTool";
import { bindFrameTool } from "./tools/frameTool";
import { bindTextTool } from "./tools/textTool";
import { bindStickyTool } from "./tools/stickyTool";

const TOOL_STAGE_CLASS: Record<BoardToolId, string> = {
  select: "is-tool-select",
  hand: "is-tool-hand",
  pen: "is-tool-pen",
  line: "is-tool-line",
  arrow: "is-tool-arrow",
  shape: "is-tool-shape",
  frame: "is-tool-frame",
  text: "is-tool-text",
  sticky: "is-tool-sticky",
  eraser: "is-tool-eraser",
};

export type ToolController = {
  setActiveTool(toolId: BoardToolId): void;
  remountTool(toolId: BoardToolId): void;
  getActiveTool(): BoardToolId;
  destroy(): void;
};

export function createToolController(host: SceneHost): ToolController {
  // Palm rejection: когда работает стилус (pointerType=pen),
  // игнорируем touch-события (ладонь) на поверхности доски.
  const activePenPointers = new Set<number>();
  let lastPenInputAt = 0;
  const PEN_TOUCH_SUPPRESS_MS = 900;

  let activeTool: BoardToolId = "select";
  let selectBinding: SelectToolBinding | null = null;
  let handBinding: HandToolBinding | null = null;
  let penBinding: PenToolBinding | null = null;
  let lineBinding: ReturnType<typeof bindLineTool> | null = null;
  let arrowBinding: ReturnType<typeof bindArrowTool> | null = null;
  let shapeBinding: ReturnType<typeof bindShapeTool> | null = null;
  let frameBinding: ReturnType<typeof bindFrameTool> | null = null;
  let textBinding: ReturnType<typeof bindTextTool> | null = null;
  let stickyBinding: ReturnType<typeof bindStickyTool> | null = null;
  let eraserBinding: EraserToolBinding | null = null;

  function clearStageToolClasses() {
    for (const el of boardCursorSurfaces(host)) {
      for (const cls of Object.values(TOOL_STAGE_CLASS)) {
        el.classList.remove(cls);
      }
    }
    clearShapeKindCursorClasses(host);
  }

  function syncShapeKindCursor() {
    syncShapeKindCursorClass(host, activeTool, getShapeSettings().kind);
  }

  function unmountActive() {
    selectBinding?.destroy();
    handBinding?.destroy();
    penBinding?.destroy();
    lineBinding?.destroy();
    arrowBinding?.destroy();
    shapeBinding?.destroy();
    frameBinding?.destroy();
    textBinding?.destroy();
    stickyBinding?.destroy();
    eraserBinding?.destroy();
    selectBinding = null;
    handBinding = null;
    penBinding = null;
    lineBinding = null;
    arrowBinding = null;
    shapeBinding = null;
    frameBinding = null;
    textBinding = null;
    stickyBinding = null;
    eraserBinding = null;
    clearStageToolClasses();
  }

  function mountTool(toolId: BoardToolId) {
    unmountActive();
    activeTool = toolId;
    for (const el of boardCursorSurfaces(host)) {
      el.classList.add(TOOL_STAGE_CLASS[toolId]);
    }
    if (toolId === "select") {
      selectBinding = bindSelectTool(host);
    } else if (toolId === "hand") {
      handBinding = bindHandTool(host);
    } else if (toolId === "pen") {
      penBinding = bindPenTool(host);
    } else if (toolId === "line") {
      lineBinding = bindLineTool(host);
    } else if (toolId === "arrow") {
      arrowBinding = bindArrowTool(host);
    } else if (toolId === "shape") {
      shapeBinding = bindShapeTool(host);
    } else if (toolId === "frame") {
      frameBinding = bindFrameTool(host);
    } else if (toolId === "text") {
      textBinding = bindTextTool(host);
    } else if (toolId === "sticky") {
      stickyBinding = bindStickyTool(host);
    } else if (toolId === "eraser") {
      eraserBinding = bindEraserTool(host);
    }
    syncShapeKindCursor();
  }

  function onShapeSettingsChange() {
    syncShapeKindCursor();
  }

  mountTool("select");

  window.addEventListener(shapeSettingsEventName(), onShapeSettingsChange);

  function isPalmTouch(ev: PointerEvent): boolean {
    if (ev.pointerType !== "touch") return false;
    if (activePenPointers.size > 0) return true;
    return Date.now() - lastPenInputAt <= PEN_TOUCH_SUPPRESS_MS;
  }

  function onSurfacePointerDownCapture(ev: PointerEvent) {
    // Тронули доску — значит работают с ней: возвращаем ей клавиатуру.
    // Инструменты гасят pointerdown, и браузер сам фокус уже не переводит,
    // поэтому после щелчка в окне звонка Ctrl+Z и Delete молчали.
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      shouldReleaseFocus(active, ev.target instanceof Element ? ev.target : null)
    ) {
      active.blur();
    }
    if (ev.pointerType === "pen") {
      activePenPointers.add(ev.pointerId);
      lastPenInputAt = Date.now();
      return;
    }
    if (!isPalmTouch(ev)) return;
    ev.preventDefault();
    ev.stopPropagation();
  }

  function onSurfacePointerMoveCapture(ev: PointerEvent) {
    // Мышь вернулась из окна звонка на доску — клавиатуру тоже возвращаем,
    // не дожидаясь щелчка: иначе Ctrl+V сразу после звонка уходил в звонок.
    const active = document.activeElement;
    if (active instanceof HTMLElement && shouldReleaseFocusOnHover(active)) {
      active.blur();
    }
    if (ev.pointerType === "pen") {
      lastPenInputAt = Date.now();
      return;
    }
    if (!isPalmTouch(ev)) return;
    ev.preventDefault();
    ev.stopPropagation();
  }

  function onWindowPointerEndCapture(ev: PointerEvent) {
    if (ev.pointerType !== "pen") return;
    activePenPointers.delete(ev.pointerId);
    lastPenInputAt = Date.now();
  }

  host.hitSurface.addEventListener("pointerdown", onSurfacePointerDownCapture, true);
  host.hitSurface.addEventListener("pointermove", onSurfacePointerMoveCapture, true);
  window.addEventListener("pointerup", onWindowPointerEndCapture, true);
  window.addEventListener("pointercancel", onWindowPointerEndCapture, true);

  return {
    setActiveTool(toolId) {
      // Всегда mount: после авто-switch на select React может оставить
      // tool=shape, и повторный R/клик с тем же id иначе был бы no-op.
      mountTool(toolId);
    },
    /** Явный remount (алиас; setActiveTool тоже всегда монтирует заново). */
    remountTool(toolId: BoardToolId) {
      mountTool(toolId);
    },
    getActiveTool: () => activeTool,
    destroy() {
      window.removeEventListener(shapeSettingsEventName(), onShapeSettingsChange);
      host.hitSurface.removeEventListener("pointerdown", onSurfacePointerDownCapture, true);
      host.hitSurface.removeEventListener("pointermove", onSurfacePointerMoveCapture, true);
      window.removeEventListener("pointerup", onWindowPointerEndCapture, true);
      window.removeEventListener("pointercancel", onWindowPointerEndCapture, true);
      unmountActive();
    },
  };
}
